import { format } from 'date-fns';
import { useFormikContext } from 'formik';

import { FormikComboBox } from '../../../cdl/ComboBox/FormikComboBox';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Label } from '../../../cdl/Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../../../cdl/RadioGroup/RadioGroup';
import { Text } from '../../../cdl/Text/Text';
import { Divider } from '../../../common/Divider/Divider';
import { translate } from '../../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { MiddleDotSeparator } from '../../../common/Separators/MiddleDotSeparator';
import { Box } from '../../../common/ui/Box';
import { useDemandDefinitionCalculationContext } from '../../hooks/useDemandDefinitionCalculationContext';
import type { DemandCalculation } from '../../types/DemandCalculation';
import type { DemandDefinitionState } from '../../types/DemandDefinitionState';
import type { DemandFleetChange } from '../../types/DemandFleetChange';
import type { DemandFleetChangeVessel } from '../../types/DemandFleetChangeVessel';
import { FleetChangeHandling } from '../../types/FleetChangeHandling';
import { DemandRecalculating } from '../DemandRecalculating';
import { DemandVolume } from '../DemandVolume';
import { ResultingDemandDrawer } from '../ResultingDemandDrawer';

interface FleetChangeVesselCardProps {
    vessel: DemandFleetChangeVessel;
    calculation: DemandCalculation;
    isDeactivated: boolean;
    isFetching: boolean;
}

export const FleetChangeVesselCard = ({
    vessel,
    calculation,
    isDeactivated,
    isFetching,
}: FleetChangeVesselCardProps) => {
    const { push } = useDrawer();
    const { categories, companies } = useDemandDefinitionCalculationContext();
    const { values, setFieldValue, setFieldTouched, isSubmitting, getFieldMeta } =
        useFormikContext<DemandDefinitionState>();
    const changeIndex = values.fleetChanges.findIndex((entry) => entry.vesselId === vessel.vesselId);
    const hasNoRecordedDemand = !isDeactivated && vessel.historicalVolume === 0;
    const change = values.fleetChanges[changeIndex] ?? {
        vesselId: vessel.vesselId,
        handling: isDeactivated ? FleetChangeHandling.EXCLUDE : FleetChangeHandling.RECORDED_ONLY,
        mirrorVesselId: null,
    };
    const mirrorError = getFieldMeta<string>(`fleetChanges.${changeIndex}.mirrorVesselId`).error;
    const isVesselRecalculating =
        isFetching &&
        (change.handling !== FleetChangeHandling.MIRROR_VESSEL || Boolean(change.mirrorVesselId)) &&
        (change.handling !== vessel.handling || (change.mirrorVesselId ?? null) !== (vessel.mirrorVesselId ?? null));
    const options = isDeactivated
        ? [
              { value: FleetChangeHandling.EXCLUDE, label: translate('demandDefinition.excludeFromDemand') },
              { value: FleetChangeHandling.INCLUDE, label: translate('demandDefinition.includeInDemand') },
          ]
        : [
              { value: FleetChangeHandling.RECORDED_ONLY, label: translate('demandDefinition.useRecordedDataOnly') },
              {
                  value: FleetChangeHandling.MIRROR_VESSEL,
                  label: translate('demandDefinition.baseVolumesOnDifferentVessel'),
              },
          ];
    const mirrorOptions = [
        ...calculation.vessels
            .filter((source) => source.vesselId !== vessel.vesselId && source.liftingCount > 0)
            .map((source) => ({ value: source.vesselId, label: source.vesselName })),
        ...companies
            .flatMap((company) => company.vessels)
            .filter(
                (source) =>
                    source.id !== vessel.vesselId &&
                    values.leavingVessels.some((entry) => entry.vesselId === source.id) &&
                    calculation.vesselVolumes[source.id] > 0 &&
                    !calculation.vessels.some((entry) => entry.vesselId === source.id)
            )
            .map((source) => ({ value: source.id, label: source.name })),
    ].sort((firstOption, secondOption) =>
        firstOption.label.localeCompare(secondOption.label, 'en', { sensitivity: 'base' })
    );
    const demand = calculation.vessels.find((entry) => entry.vesselId === vessel.vesselId);
    const adjustedVolume = demand?.summary.adjustedVolume ?? 0;
    let selectedOption = options.find((option) => option.value === change.handling)?.label;
    switch (change.handling) {
        case FleetChangeHandling.RECORDED_ONLY:
            selectedOption = translate('demandDefinition.volumesBasedOnRecordedData');
            break;
        case FleetChangeHandling.MIRROR_VESSEL: {
            const mirrorVessel = mirrorOptions.find((option) => option.value === change.mirrorVesselId);
            if (mirrorVessel) {
                selectedOption = translate('demandDefinition.volumesBasedOnVesselName', {
                    vesselName: mirrorVessel.label,
                });
            }
            break;
        }
    }
    const date = isDeactivated ? vessel.dateDeactivated : vessel.dateAdded;
    const formattedDate = date ? format(new Date(date), 'MMM d, yyyy') : '';
    const dateLabel = isDeactivated
        ? translate('demandDefinition.deactivatedOnDate', { date: formattedDate })
        : translate('demandDefinition.addedOnDate', { date: formattedDate });

    const updateChange = (nextChange: DemandFleetChange) => {
        const fleetChanges =
            changeIndex < 0
                ? [...values.fleetChanges, nextChange]
                : values.fleetChanges.map((entry, index) => (index === changeIndex ? nextChange : entry));
        setFieldValue('fleetChanges', fleetChanges);
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={12}
            border="1px solid"
            borderColor="border.muted"
            borderRadius={8}
            backgroundColor="background.default"
        >
            <Box display="flex" flexDirection="column" gap={12} padding={16}>
                <Box display="flex" flexDirection="column" gap={4}>
                    <Text variant="small" fontWeight={600}>
                        {vessel.vesselName} <Text color="foreground.subtle">({vessel.imo})</Text>
                    </Text>
                    <Text variant="extraSmall" color="foreground.muted">
                        {date ? (
                            <>
                                {dateLabel}
                                <MiddleDotSeparator />
                            </>
                        ) : null}
                        {translate('demandDefinition.historicalDataAvailableFromTo', {
                            from: format(new Date(vessel.dataFrom), 'MMM d, yyyy'),
                            to: format(new Date(vessel.dataTo), 'MMM d, yyyy'),
                        })}
                    </Text>
                </Box>
                <RadioGroup
                    value={change.handling}
                    disabled={isVesselRecalculating || isSubmitting}
                    onValueChange={(value) => {
                        const option = options.find((entry) => entry.value === value);
                        if (option) {
                            if (
                                change.handling === FleetChangeHandling.MIRROR_VESSEL &&
                                option.value !== change.handling
                            ) {
                                setFieldTouched(`fleetChanges.${changeIndex}.mirrorVesselId`, false, false);
                            }
                            updateChange({
                                vesselId: vessel.vesselId,
                                handling: option.value,
                                mirrorVesselId:
                                    option.value === FleetChangeHandling.MIRROR_VESSEL ? change.mirrorVesselId : null,
                            });
                        }
                    }}
                >
                    {options.map((option) => (
                        <RadioGroupItem key={option.value}>
                            <Radio value={option.value} />
                            <Label>{option.label}</Label>
                        </RadioGroupItem>
                    ))}
                </RadioGroup>
                {change.handling === FleetChangeHandling.MIRROR_VESSEL ? (
                    <Box paddingLeft={24}>
                        <FormikComboBox
                            name={`fleetChanges.${changeIndex}.mirrorVesselId`}
                            label={translate('global.coreData.vessel')}
                            placeholder={translate('demandDefinition.selectVesselToMirror')}
                            options={mirrorOptions}
                            disabled={isVesselRecalculating || isSubmitting}
                            noOptionsMessage={translate('demandDefinition.noVesselsWithHistoricalDataAvailable')}
                        />
                    </Box>
                ) : null}
            </Box>
            <Divider />
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={8}
                paddingX={16}
                paddingBottom={16}
            >
                <Text variant="small" color="foreground.default">
                    {translate('demandDefinition.resultingDemand')}
                </Text>
                <Box display="flex" alignItems="center" gap={8}>
                    {isVesselRecalculating ? (
                        <DemandRecalculating variant="small" />
                    ) : (
                        <Text variant="small" fontWeight={600}>
                            <DemandVolume volume={adjustedVolume} />
                        </Text>
                    )}
                    <IconButton
                        type="button"
                        Icon={IconInfoCircle}
                        size="small"
                        aria-label={translate('demandDefinition.viewDetails')}
                        tooltipLabel={translate('demandDefinition.viewDetails')}
                        disabled={
                            Boolean(mirrorError) ||
                            isVesselRecalculating ||
                            (hasNoRecordedDemand && !change.mirrorVesselId) ||
                            (isDeactivated && adjustedVolume === 0)
                        }
                        onClick={() =>
                            push({
                                header: { title: translate('demandDefinition.resultingDemand') },
                                width: '800px',
                                content: (
                                    <ResultingDemandDrawer
                                        vesselName={vessel.vesselName}
                                        selectedOption={selectedOption}
                                        showChanges={
                                            vessel.historicalVolume > 0 &&
                                            change.handling === FleetChangeHandling.MIRROR_VESSEL &&
                                            Boolean(change.mirrorVesselId)
                                        }
                                        historicalVolume={vessel.historicalVolume}
                                        demand={demand}
                                        categories={categories}
                                    />
                                ),
                            })
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};
